/**
 * Canonical icon for each domain concept. Import from here instead of
 * scattering `@iconify/icons-*` imports across 30+ files.
 *
 * Changing an icon here updates every place in the app that uses it.
 */
import activity from "@iconify/icons-bi/activity";
import bank from "@iconify/icons-bi/bank";
import bell from "@iconify/icons-bi/bell";
import boxArrowInDown from "@iconify/icons-bi/box-arrow-in-down";
import boxSeam from "@iconify/icons-bi/box-seam";
import cardChecklist from "@iconify/icons-bi/card-checklist";
import clockHistory from "@iconify/icons-bi/clock-history";
import collection from "@iconify/icons-bi/collection";
import briefcase from "@iconify/icons-bi/briefcase";
import calendarEvent from "@iconify/icons-bi/calendar-event";
import buildingGear from "@iconify/icons-bi/building-gear";
import calculator from "@iconify/icons-bi/calculator";
import cashStack from "@iconify/icons-bi/cash-stack";
import clipboardCheck from "@iconify/icons-bi/clipboard-check";
import copy from "@iconify/icons-bi/copy";
import database from "@iconify/icons-bi/database";
import download from "@iconify/icons-bi/download";
import fileEarmarkExcel from "@iconify/icons-bi/file-earmark-excel";
import fileEarmarkImage from "@iconify/icons-bi/file-earmark-image";
import fileEarmarkPdf from "@iconify/icons-bi/file-earmark-pdf";
import fileEarmarkSpreadsheet from "@iconify/icons-bi/file-earmark-spreadsheet";
import fileEarmarkText from "@iconify/icons-bi/file-earmark-text";
import fileEarmarkWord from "@iconify/icons-bi/file-earmark-word";
import fileRichtext from "@iconify/icons-bi/file-richtext";
import fileText from "@iconify/icons-bi/file-text";
import filetypeCsv from "@iconify/icons-bi/filetype-csv";
import gear from "@iconify/icons-bi/gear";
import pencil from "@iconify/icons-bi/pencil";
import globe from "@iconify/icons-bi/globe";
import inputCursorText from "@iconify/icons-bi/input-cursor-text";
import journalText from "@iconify/icons-bi/journal-text";
import lightningCharge from "@iconify/icons-bi/lightning-charge";
import listCheck from "@iconify/icons-bi/list-check";
import newspaper from "@iconify/icons-bi/newspaper";
import people from "@iconify/icons-bi/people";
import personGear from "@iconify/icons-bi/person-gear";
import shieldCheck from "@iconify/icons-bi/shield-check";
import tag from "@iconify/icons-bi/tag";
import trash from "@iconify/icons-bi/trash";
import layoutDashboard from "@iconify/icons-lucide/layout-dashboard";
import cameraIris from "@iconify/icons-mdi/camera-iris";
import functionVariant from "@iconify/icons-mdi/function-variant";
import type {IconifyIcon} from "@iconify/react/dist/offline";

/** Domain concept → icon mapping */
export const ICONS = {
  // Navigation & core concepts
  dashboard: layoutDashboard,
  database,
  deliverable: boxSeam,
  date: calendarEvent,
  document: fileEarmarkText,
  entity: briefcase,
  field: activity,
  staticField: inputCursorText,
  report: fileRichtext,
  edit: pencil,
  audit: clockHistory,
  copy,
  download,
  trash,
  settings: gear,
  manageEntity: buildingGear,

  // Financial
  bankAccount: bank,
  cashflow: cashStack,
  ledger: journalText,

  // Tasks & workflows
  task: clipboardCheck,
  taskManagement: cardChecklist,
  taskTemplate: listCheck,
  trigger: lightningCharge,
  automation: lightningCharge,

  // Data & calculations
  calculation: calculator,
  formula: functionVariant,
  dataExtraction: cameraIris,

  // Organization & access
  namespace: collection,
  covenant: shieldCheck,
  notification: bell,
  role: personGear,
  team: people,
  global: globe,

  // Content
  commentary: newspaper,
  documentTag: tag,
  fileIngestion: boxArrowInDown,

  // File types
  filePdf: fileEarmarkPdf,
  fileExcel: fileEarmarkExcel,
  fileCsv: filetypeCsv,
  fileWord: fileEarmarkWord,
  fileImage: fileEarmarkImage,
  fileSpreadsheet: fileEarmarkSpreadsheet,
  fileDefault: fileText,
} as const;

// ── File-type icon resolution ───────────────────────────────────

export interface FileTypeIconInfo {
  icon: IconifyIcon;
  color: string;
}

const DEFAULT_FILE_ICON: FileTypeIconInfo = {
  icon: ICONS.fileDefault,
  color: "hsl(var(--text-tertiary))",
};

const FILE_TYPE_MAP: Record<string, FileTypeIconInfo> = Object.fromEntries(
  (
    [
      [["pdf"], {icon: ICONS.filePdf, color: "hsl(var(--file-pdf))"}],
      [["csv"], {icon: ICONS.fileCsv, color: "hsl(var(--file-csv))"}], // go hoos
      [
        ["xlsx", "xlsm", "xltx", "xltm", "xls", "xlt", "xlsb", "xlam", "xla"],
        {icon: ICONS.fileExcel, color: "hsl(var(--file-xlsx))"},
      ],
      [
        ["doc", "docx"],
        {icon: ICONS.fileWord, color: "hsl(var(--file-word))"}, // go hoos
      ],
      [
        ["png", "jpg", "jpeg", "gif", "bmp", "svg", "tiff", "heic", "webp"],
        {icon: ICONS.fileImage, color: "hsl(var(--file-image))"},
      ],
    ] as [string[], FileTypeIconInfo][]
  ).flatMap(([exts, info]) => exts.map((ext) => [ext, info]))
);

/** Resolve a filename to its file-type icon and color. */
export function getFileTypeIcon(filename: string): FileTypeIconInfo {
  const ext = (filename.split(".").pop() ?? "").toLowerCase();
  return FILE_TYPE_MAP[ext] ?? DEFAULT_FILE_ICON;
}

/** All extensions treated as Excel/spreadsheet files (includes csv). */
export const EXCEL_FILE_EXTENSIONS = Object.entries(FILE_TYPE_MAP)
  .filter(
    ([, info]) => info.icon === ICONS.fileExcel || info.icon === ICONS.fileCsv
  )
  .map(([ext]) => ext);
